import React from 'react';
import { t } from '@lingui/macro';

import type { SortingTypes } from '@decisiv/ui-components/lib/components/Table';
import { Column } from '@decisiv/ui-components/lib/components/Table/types';

import { sortFactory } from 'utils/sortFactory';
import { columnWidth, MIN_COLUMN_WIDTHS } from 'utils/tableColumnWidths';

import TeamTypeCell from 'pages/Teams/Cells/TeamTypeCell';
import IdCell from 'pages/Teams/Cells/IdCell';

import CreatedAtCell from 'components/CreatedAtCell';
import EnabledAssetsCell from 'pages/Teams/Cells/EnabledAssetsCell';
import MembersCell from 'pages/Teams/Cells/MembersCell';
import ActionsCell from 'pages/Teams/Cells/ActionsCell';
import CreatedByCell from 'components/CreatedByCell';
import type { ManagedTeamsType } from '.';

import AccountIdCell from './Cells/AccountIdCell';
import OwnerEmailCell from './Cells/OwnerEmailCell';
import ApiUserCell from './Cells/ApiUserCell';
import OemSegmentNameCell from './Cells/OemSegmentNameCell';
import TeamNameCell from './Cells/TeamNameCell';
import PlatformAccountsCountCell from './Cells/PlatformAccountsCell';
import TeamActionsCell from './Cells/TeamActionsCell';
import TeamTabCountCell from './Cells/TeamTabCountCell';

type CellProps = { rowData: ManagedTeamsType };

// PD-5930 UX review: with the flag on, the counts open the Team page tabs.
const AccountsTabCountCell = ({ rowData }: CellProps) => (
  <TeamTabCountCell
    teamId={rowData.id}
    tab="accounts"
    count={rowData.totals?.platformAccounts}
  />
);
const DepotsTabCountCell = ({ rowData }: CellProps) => (
  <TeamTabCountCell
    teamId={rowData.id}
    tab="depots"
    count={rowData.totals?.assetGroupings?.total}
  />
);
const AssetsTabCountCell = ({ rowData }: CellProps) => (
  <TeamTabCountCell
    teamId={rowData.id}
    tab="assets"
    count={rowData.totals?.assetGroupings?.enabledAssets}
  />
);
const SingleLineCreatedAtCell = (props: CellProps) => (
  <CreatedAtCell {...props} singleLine />
);

const TOP_ALIGNED = {
  getCellAttrs: () => ({ style: { verticalAlign: 'top' } }),
};

interface GenerateColumnsParams {
  isSuperUser?: boolean;
  /** FEATURE_FLAGS.PD_5874_FLEET_ASSET_MANAGEMENT */
  fleetAssetManagementEnabled?: boolean;
  amountOfOemSegmentsManaged: number;
  onSort: (fieldName: string, direction: SortingTypes) => void;
}

export const generateColumns = ({
  onSort,
  amountOfOemSegmentsManaged,
  isSuperUser = false,
  fleetAssetManagementEnabled = false,
}: GenerateColumnsParams) => {
  const superuserColumns = [
    {
      fixed: true,
      name: 'id',
      title: t`Team ID`,
      DataCell: IdCell,
      sortBy: sortFactory('ID', onSort),
    },
    {
      name: 'type',
      title: t`Team Type`,
      DataCell: TeamTypeCell,
      sortBy: sortFactory('TEAM_TYPE', onSort),
    },
    {
      fixed: true,
      name: 'enabled',
      title: t`Status`,
      DataCell: (props: React.ComponentProps<typeof ActionsCell>) => (
        <ActionsCell {...props} showDataRefresh={false} />
      ),
    },
  ] satisfies Column<ManagedTeamsType>[];

  const nameColumn = {
    name: 'name',
    title: t`Team Name`,
    DataCell: TeamNameCell,
    sortBy: sortFactory('NAME', onSort),
  } satisfies Column<ManagedTeamsType>;

  const oemSegmentColumns =
    amountOfOemSegmentsManaged > 1
      ? [
          {
            name: 'oemSegmentName',
            title: t`OEM Segment Name`,
            DataCell: OemSegmentNameCell,
            sortBy: sortFactory('OEM_SEGMENT_NAME', onSort),
          },
        ]
      : [];

  // KDS Table pins `fixed` columns only when they are grouped at the start or
  // the end, so with PD-5874 the name leads and the actions column trails.
  const leadingColumns = fleetAssetManagementEnabled
    ? [{ ...nameColumn, fixed: true }, ...oemSegmentColumns]
    : [...oemSegmentColumns, nameColumn];

  const generalColumns = [
    ...leadingColumns,
    {
      name: 'portalId',
      title: t`Account ID`,
      DataCell: AccountIdCell,
      sortBy: sortFactory('DEALER_GROUP_ID', onSort),
    },
    {
      name: 'platformAccounts',
      title: t`Accounts`,
      DataCell: PlatformAccountsCountCell,
      sortBy: sortFactory('PLATFORM_ACCOUNTS_COUNT', onSort),
    },
    // Not sortable: SentinelTeamsSortColumns has no depot count column.
    ...(fleetAssetManagementEnabled
      ? [{ name: 'depots', title: t`Depots`, DataCell: DepotsTabCountCell }]
      : []),
    {
      name: 'assets',
      title: t`Assets`,
      DataCell: EnabledAssetsCell,
      sortBy: sortFactory('ENABLED_ASSET_COUNT', onSort),
    },
    {
      name: 'members',
      title: t`Users`,
      DataCell: MembersCell,
      sortBy: sortFactory('MEMBERS_COUNT', onSort),
    },
    {
      name: 'createdBy',
      title: t`Created By`,
      DataCell: CreatedByCell,
      sortBy: sortFactory('CREATED_BY_NAME', onSort),
    },
    {
      name: 'createdAt',
      title: t`Created On`,
      DataCell: CreatedAtCell,
      sortBy: sortFactory('CREATED_AT', onSort),
    },
    {
      name: 'owner',
      title: t`Owner`,
      DataCell: OwnerEmailCell,
      sortBy: sortFactory('OWNER_USERNAME', onSort),
    },
    {
      name: 'apiUsername',
      title: t`API User`,
      DataCell: ApiUserCell,
      sortBy: sortFactory('API_USER', onSort),
    },
  ] satisfies Column<ManagedTeamsType>[];

  const actionsColumns = fleetAssetManagementEnabled
    ? [
        {
          fixed: true,
          name: 'actions',
          title: '',
          DataCell: TeamActionsCell,
        },
      ]
    : [];

  const columns = [
    ...generalColumns,
    ...(isSuperUser ? superuserColumns : []),
    ...actionsColumns,
  ];
  if (!fleetAssetManagementEnabled) return columns;

  // PD-5930 UX review: top-aligned cells, and wider name, date and API user
  // columns so they rarely wrap.
  const overrides: Record<string, Partial<Column<ManagedTeamsType>>> = {
    name: columnWidth({ minWidth: MIN_COLUMN_WIDTHS.wider }),
    platformAccounts: { DataCell: AccountsTabCountCell },
    assets: { DataCell: AssetsTabCountCell },
    createdAt: {
      DataCell: SingleLineCreatedAtCell,
      ...columnWidth({ minWidth: MIN_COLUMN_WIDTHS.wide }),
    },
    apiUsername: columnWidth({ minWidth: MIN_COLUMN_WIDTHS.wider }),
  };
  return columns.map((column) => ({
    ...TOP_ALIGNED,
    ...column,
    ...overrides[column.name],
  }));
};
